import { useCallback, useEffect, useRef, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { TimeEntry } from "@/api/types";
import { createDedupedFetcher } from "@/hooks/useDedupedFetch";

export type PendingTimeStatus = "loading" | "ready" | "error";

export interface UsePendingTimeResult {
  /** `null` until resolved and on failure — never `[]`, which is the real and different
   *  answer "the queue is empty, there is nothing to review". */
  entries: TimeEntry[] | null;
  status: PendingTimeStatus;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * De-dupes concurrent `GET /api/demo/time/pending` calls, the same way
 * `useSelectedEntity.ts`'s own `fetchEntitiesDeduped` does and for the identical reason:
 * React 18 StrictMode mounts, cleans up, and re-mounts every effect against the Vite dev
 * server this suite runs on (see `AppShell.tsx`'s own remark on this) — TWO GET requests
 * for the same queue, launched in the same tick.
 *
 * THIS HOOK'S OWN REGRESSION, caught by a trace (row-actions.spec.ts, gate 32): the first
 * mount's request and the second mount's request go out at the identical millisecond: one
 * gets `AbortController.abort()`ed client-side on cleanup, the other resolves — but
 * `abort()` only cancels this component instance's own wait on the response, not a
 * request already in flight to the server. Two near-simultaneous reads of the same
 * just-written row is exactly the hazard `PayRunPrint.tsx`'s own de-dupe comment already
 * names ("the first client-aborted request can already be committed server-side before
 * the abort lands") — here it cost a read its freshness rather than a check number.
 * Sharing the in-flight PROMISE (never a cached result) collapses the doubled mount back
 * to the ONE real round trip a single mount was always meant to make, closing the window
 * where a second identical request could race the first for the same, freshly-written
 * queue.
 *
 * ef-048-0c: the promise-sharing itself now lives in `useDedupedFetch.ts`'s
 * `createDedupedFetcher`, shared with `useSelectedEntity.ts` — deliberately still NOT a
 * persistent cache there either, so the NEXT independent read (a poll tick, `reload()`
 * after a decision, or a later mount) always asks the server again rather than
 * replaying a stale queue. This is also what keeps a poll tick and a mid-decision
 * `reload()` landing at the same moment from ever doubling up: whichever calls
 * `fetchPendingDeduped()` first is the one round trip both of them get.
 */
const fetchPendingDeduped = createDedupedFetcher(() => demoApi.getPendingTime());

/**
 * True when two reads of the queue carry identical rows in identical order.
 *
 * Backs the poll's "no state churn on an unchanged payload" requirement: `TimeEntry[]` is
 * plain, server-serialized JSON with a stable field order per response (never hand-built
 * client-side), so a straight stringify compare is exact and cheap for a queue this size —
 * a farm's open review list, not an unbounded feed. Worth avoiding specifically because
 * `TimeApprovalQueue.tsx`'s `QueueRow` holds open, uncommitted local state (a half-typed
 * reject note, an open Allocate panel) that a needless re-render risks resetting mid-edit.
 */
function sameEntries(a: TimeEntry[], b: TimeEntry[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Poll cadence while the queue is mounted and the tab is visible.
 *
 * Gates 30–35 each lost one row-actions phone test to "no queue row rendered for <memo>"
 * under full-suite contention: a worker files by API while the owner already has Payroll
 * open, and this hook's earlier fix — a SINGLE 1.5s confirmation re-read, now removed —
 * could still land before the write under enough load (measured: one request 189ms behind
 * its sibling's already-200'd POST, and still short the row). A repeating poll succeeds
 * where one extra read cannot: several tries land inside the suite's 15s expect budget
 * instead of one, without hammering the API for the rest of the time the tab sits open.
 */
const POLL_INTERVAL_MS = 4_000;
/** Backoff ceiling on repeated poll failures — the same rhythm `useEntries.ts`'s
 *  stream-reopen and `offline/drainTriggers.ts` already use (2s doubling, capped at 30s),
 *  so one retry cadence covers the codebase. Reset to `POLL_INTERVAL_MS` the moment a poll
 *  succeeds again. */
const MAX_POLL_BACKOFF_MS = 30_000;

/**
 * The owner's review queue — GET /api/demo/time/pending, oldest first so it drains.
 *
 * Owner-only server-side (403 for an employee login). This hook does not check that and
 * must not: a client-side role check is a rendering decision, and treating it as the
 * control is how the real one stops being maintained. The Payroll screen it feeds is
 * already behind the owner shell.
 *
 * Re-reads after every decision rather than removing the row locally. A 409 — somebody
 * else already decided it, or the same thumb tapped twice — means this list is stale,
 * and the honest response to a stale list is to fetch it again.
 *
 * SELF-REFRESHING WHILE MOUNTED. There is no SSE/EventSource anywhere in this hook's own
 * dependency chain (`usePendingTime`, `TimeApprovalQueue`, `Payroll`, `usePayrollRoster`,
 * `usePayrollRun`, `useJobCodes`) to piggyback on — `useEntries.ts`'s ledger stream is a
 * different screen (the laptop dashboard) and carries entry events, never time-entry ones
 * — so this polls `GET /api/demo/time/pending` on its own timer instead of waiting for a
 * farmer to reload the page. Paused while `document.hidden` (a backgrounded Payroll tab
 * has nobody to show a fresher queue to, and every tick is a real request against the one
 * API the whole suite shares) and read again immediately on return to the tab.
 */
export function usePendingTime(): UsePendingTimeResult {
  const [entries, setEntries] = useState<TimeEntry[] | null>(null);
  const [status, setStatus] = useState<PendingTimeStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  // Mirrors `entries` without being a dependency of anything — `sameEntries` needs the
  // last-rendered payload to compare a silent poll's result against, and closing over the
  // `entries` state value directly would give `load` a new identity every render (it
  // would need `entries` in its own deps), which would tear down and restart the poll
  // effect below on every tick instead of running once per mount.
  const entriesRef = useRef<TimeEntry[] | null>(null);

  // Shared, de-duped fetch (see `fetchPendingDeduped` above) — this instance never
  // aborts it on its own cleanup, because another consumer mounted in the same wave
  // (StrictMode's own second mount, a concurrent poll tick, or `reload()`) may still be
  // awaiting that exact promise. `cancelled` still guards the setState calls below, so an
  // instance that unmounted before the shared request settled never touches state after
  // the fact.
  //
  // `silent`: a poll tick never flips an already-rendered queue back to "loading", and a
  // poll tick's own failure never turns into a visible error over a queue that already
  // rendered fine — only the mount's own first read and an explicit `reload()` do that.
  // Resolves to whether this read succeeded, so the poll loop below knows whether to reset
  // or extend its backoff.
  const load = useCallback(
    async (cancelledRef?: { current: boolean }, silent = false): Promise<boolean> => {
      if (!silent) setStatus("loading");
      try {
        const resolved = await fetchPendingDeduped();
        if (cancelledRef?.current) return false;
        if (silent && entriesRef.current !== null && sameEntries(entriesRef.current, resolved)) {
          // Unchanged since the last render: a successful read, nothing to re-render.
          return true;
        }
        entriesRef.current = resolved;
        setEntries(resolved);
        setStatus("ready");
        setError(null);
        return true;
      } catch (err) {
        if (cancelledRef?.current) return false;
        if (silent) return false; // swallowed — see the `silent` remark above
        entriesRef.current = null;
        setEntries(null);
        setStatus("error");
        setError(problemMessage(err, "Could not load the time-approval queue."));
        return false;
      }
    },
    [],
  );

  useEffect(() => {
    const cancelledRef = { current: false };
    void load(cancelledRef);

    let timer: ReturnType<typeof setTimeout> | undefined;
    let delay: number = POLL_INTERVAL_MS;

    function schedule(ms: number): void {
      if (cancelledRef.current) return;
      timer = setTimeout(() => void tick(), ms);
    }

    async function tick(): Promise<void> {
      if (cancelledRef.current) return;
      if (document.hidden) {
        // Still paused: check back at the same cadence rather than a tighter one, so a
        // tab left open in the background costs nothing extra while it stays hidden.
        schedule(delay);
        return;
      }
      const ok = await load(cancelledRef, true);
      delay = ok ? POLL_INTERVAL_MS : Math.min(delay * 2, MAX_POLL_BACKOFF_MS);
      schedule(delay);
    }

    schedule(delay);

    // Coming back to a backgrounded tab reads immediately rather than waiting out
    // whatever is left of the paused timer — the same "read now, not on the next
    // scheduled tick" reasoning `useEntries.ts` gives its own stream-reopen path.
    function onVisibilityChange(): void {
      if (document.hidden || cancelledRef.current) return;
      if (timer !== undefined) clearTimeout(timer);
      void tick();
    }
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelledRef.current = true;
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [load]);

  const reload = useCallback(async () => {
    await load();
  }, [load]);

  return { entries, status, error, reload };
}
